import {ApiClient} from '@hconnect/apiclient'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {DIRTY_POLL_INTERVAL} from '../consts'
import {
  ExpertSystemEquipmentType,
  MonthDataColumns,
  MtmKpiValueQueryKey,
  MtmWithAggregatesResponse,
  WeightedAverageValue
} from '../types'
import {mapMonthsData} from '../utils'

const BEGINNING_OF_A_YEAR = '-01-01'

export type MtmKpiResult = {
  columns: MonthDataColumns
  weightedAverages: WeightedAverageValue[]
}

export const useMtmKpiValue = (
  kpiTargetEquipmentType: ExpertSystemEquipmentType,
  year: number,
  options?: Omit<
    UseQueryOptions<MtmWithAggregatesResponse, AxiosError, MtmKpiResult>,
    'queryKey' | 'queryFn'
  >
) => {
  const from = `${year}${BEGINNING_OF_A_YEAR}`
  const to = `${year + 1}${BEGINNING_OF_A_YEAR}`

  return useQuery<MtmWithAggregatesResponse, AxiosError, MtmKpiResult>({
    queryKey: [MtmKpiValueQueryKey, kpiTargetEquipmentType, year],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<MtmWithAggregatesResponse>(
        `/expert-system/kpis/${kpiTargetEquipmentType}/mtm-with-aggregates`,
        {
          params: {from, to},
          signal
        }
      )

      return response.data
    },
    select: ({rows, weightedAverages}: MtmWithAggregatesResponse) => ({
      columns: mapMonthsData(rows),
      weightedAverages
    }),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return DIRTY_POLL_INTERVAL
      const hasDirty = data.rows.some((row) => row.isDirty)
      return hasDirty ? DIRTY_POLL_INTERVAL : false
    },
    ...options
  })
}
